import type { ReactNode } from 'react';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

/**
 * Extract error message string from TanStack Form field errors.
 * Handles both string errors and StandardSchemaV1Issue objects.
 */
export function getFieldError(errors: unknown[]): string | undefined {
  const first = errors[0];
  if (!first) return undefined;
  if (typeof first === 'string') return first;
  if (typeof first === 'object' && first !== null && 'message' in first) {
    return (first as { message: string }).message;
  }
  return String(first);
}

interface FormFieldProps {
  label?: string;
  required?: boolean;
  error?: string;
  helperText?: string;
  className?: string;
  children: ReactNode;
}

export function FormField({ label, required, error, helperText, className, children }: FormFieldProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      {label && (
        <Label className={cn(error && 'text-destructive')}>
          {label}
          {required && <span className="text-destructive ml-0.5">*</span>}
        </Label>
      )}
      {children}
      {(error || helperText) && (
        <p className={cn('text-xs', error ? 'text-destructive' : 'text-grey-500')}>
          {error || helperText}
        </p>
      )}
    </div>
  );
}
